import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit } from '@angular/core';
import { ConfirmService, KeycloakService, MenuItem, ModalService, ToastService } from '@atsystems/ui';
import { UserSettingsApiService } from './common/api/user-settings-api.service';
import { applyUserConfig } from './common/helpers/apply-user-config';
import { ThemeService } from './ui/services/theme.service';
import packageInfo from 'package.json';
import { environment } from 'src/environments/environment';
import { NetworkStatusService } from './common/services/network-status.service';
import { Router } from '@angular/router';
import { Roles } from './common/helpers/roles';
import { getMenuItems } from './common/consts/menu-items';
import { SpotlightService } from './ui/modules/spotlight/spotlight.service';
import { SpotlightComponent } from './ui/modules/spotlight/spotlight.component';
import { ServicesHealthService } from './common/services/service-health.service';
import { ProductTourService } from './ui/services/product-tour/product-tour.service';
import { PRODUCT_TOUR_CLOSE_MODAL_ACTION } from './ui/services/product-tour/product-tour.consts';
import { TrackerUiService } from './pages/tracker-v2/tracker-ui.service';

@Component({
  selector: 'ats-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppComponent implements OnInit {
  private readonly spotlightService = inject(SpotlightService);
  private readonly keycloakService = inject(KeycloakService);
  public readonly confirmService = inject(ConfirmService);
  private readonly themeService = inject(ThemeService);
  private readonly toastService = inject(ToastService);
  private readonly modalService = inject(ModalService);
  private readonly networkStatusService = inject(NetworkStatusService);
  private readonly router = inject(Router);
  private readonly servicesHealth = inject(ServicesHealthService);
  private readonly productTourService = inject(ProductTourService);

  public partnersMenuItems: MenuItem[] = [
    {
      id: 'partners.tracker',
      label: 'Tracker',
      icon: 'i i-table',
      route: 'partners/tracker',
      expanded: false,
      roles: Roles.PARTNERS_VIEW_CPM_BASE,
    },
    {
      id: 'partners.internal',
      label: 'Internal',
      icon: 'i i-home',
      route: '',
      expanded: false,
      roles: Roles.FRONT_ACCESS,
    },
  ];

  public readonly internalAccessRole = Roles.FRONT_ACCESS;

  private readonly userSettings = inject(UserSettingsApiService);

  public baseMenu: MenuItem[] = getMenuItems(this);

  private readonly applied = computed(() =>
    applyUserConfig(this.baseMenu, this.userSettings.menuConfig(), item => this.canView(item)),
  );
  public menuItems = computed(() => this.applied().menu);
  public favoriteItems = computed(() => this.applied().favorites);
  public favoriteIds = computed(() => this.applied().favoriteIds);
  public collapsed = computed(() => this.userSettings.menuConfig()?.collapsed ?? false);

  private canView(item: MenuItem): boolean {
    return !item.roles?.length || this.keycloakService.hasResourceRole(item.roles);
  }

  public onFavoriteToggle(id: string): void {
    const config = this.userSettings.currentConfig();
    const favorites = config.favorites.includes(id)
      ? config.favorites.filter(favId => favId !== id)
      : [...config.favorites, id];
    this.userSettings.saveMenuConfig({ ...config, favorites });
  }

  public onMenuStateChange(state: { expanded: boolean }): void {
    const config = this.userSettings.currentConfig();
    const collapsed = !state.expanded;
    if (config.collapsed === collapsed) return;
    this.userSettings.saveMenuConfig({ ...config, collapsed });
  }

  public onOrderChange(order: string[]): void {
    this.userSettings.saveMenuConfig({ ...this.userSettings.currentConfig(), order });
  }

  public onFavoritesReorder(favorites: string[]): void {
    this.userSettings.saveMenuConfig({ ...this.userSettings.currentConfig(), favorites });
  }

  public isLightTheme = this.themeService.isLightTheme;

  public version = '';

  public showIsOffline = false;
  public isOfflineMinimize = false;
  private offlineHandle(): void {
    this.networkStatusService.isOnline$.subscribe(status => (this.showIsOffline = !status));
  }

  constructor() {
    effect(() => {
      const profile = this.keycloakService.profile();
      if (profile?.email) {
        localStorage.setItem('email', profile.email);
      }
    });
    this.version = packageInfo.version + (environment.env == 'dev' ? '-dev' : '');
    import('./ui/services/product-tour/tours').then(m => m.registerAllTours(this.productTourService));
    inject(TrackerUiService);
  }

  public signOut(): void {
    this.keycloakService.logout();
  }

  public get isPartners(): boolean {
    return this.router.url.includes('partners');
  }

  public toggleOfflineMessageSize(): void {
    this.isOfflineMinimize = !this.isOfflineMinimize;
  }

  public ngOnInit(): void {
    this.offlineHandle();

    this.productTourService.registerAction('sidebar.expand', () => {
      const config = this.userSettings.currentConfig();
      if (config.collapsed) this.userSettings.saveMenuConfig({ ...config, collapsed: false });
    });

    this.productTourService.registerAction(PRODUCT_TOUR_CLOSE_MODAL_ACTION, () => this.productTourService.closeActiveModal());
  }

  public toggleTheme(): void {
    if (this.isLightTheme()) {
      this.themeService.setDarkTheme();
    } else {
      this.themeService.setLightTheme();
    }
  }

  public showError(): void {
    this.toastService.add({ summary: 'Success', detail: 'Bundle(s) is deleted', severity: 'success' });
  }

  public async openSettingsModalV2(): Promise<void> {
    const { SettingsV2ModalComponent } = await import('./pages/settings-modal-v2/settings-v2-modal.component');
    this.modalService.open(SettingsV2ModalComponent);
  }

  public async openApiKeysModal(): Promise<void> {
    if (!this.keycloakService.hasResourceRole(Roles.API_KEY_VIEWER)) return;

    const { ApiKeysComponent } = await import('./pages/api-keys/api-keys.component');
    this.modalService.open(ApiKeysComponent);
  }

  public openSpotlightModal(): void {
    this.spotlightService.open(SpotlightComponent);
  }

  public openDino(): void {
    window.open('chrome://dino', '_blank');
  }
}
